import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, AlertCircle, AlertTriangle, CheckCircle, Loader, ExternalLink, GripVertical } from 'lucide-react';
import ComparisonTable from '../components/ComparisonTable';
import { DecisionRenderer, DecisionSection, AIInsightsSection } from '../components/decision';
import { parseLogicData, parseVibeData, parseUniverseData } from '../utils/parseComparisonData';
import { extractSectionSummaries } from '../utils/extractSectionSummaries';

const TestCaseComparison = () => {
  const { testId } = useParams();
  const [expectedData, setExpectedData] = useState(null);
  const [actualData, setActualData] = useState(null);
  const [validationResults, setValidationResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedError, setSelectedError] = useState(null);
  const [triageStatus, setTriageStatus] = useState({});
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const modalRef = useRef(null);

  useEffect(() => {
    loadComparisonData();
  }, [testId]);
  
  // Load triage status after validation results are available
  useEffect(() => {
    if (validationResults) {
      loadExistingTriageStatus();
    }
  }, [testId, validationResults]);

  const loadExistingTriageStatus = async () => {
    try {
      // Load existing triage data from database
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/test-issues/by-test-case/${testId.toLowerCase()}`
      );
      
      if (response.ok) {
        const triageData = await response.json();
        
        // Build triage status map keyed by error signature
        const statusMap = {};
        
        // Only include triages from today (to avoid showing old test run triages)
        const today = new Date().toISOString().split('T')[0];
        
        triageData.forEach(issue => {
          const issueDate = issue.updated_at?.split('T')[0] || issue.created_at?.split('T')[0];
          
          // Only load recent triages (from today or yesterday)
          const daysDiff = issueDate ? Math.floor((new Date() - new Date(issueDate)) / (1000 * 60 * 60 * 24)) : 999;
          
          if (daysDiff <= 1) {
            // Create key from section, type, and description
            const key = `${issue.section}-${issue.error_type}-${issue.description}`;
            statusMap[key] = {
              action: issue.status,
              notes: issue.notes || '',
              issueId: issue.github_issue_number,
              issueUrl: issue.github_issue_url
            };
          }
        });
        
        setTriageStatus(statusMap);
        console.log(`✅ Loaded ${Object.keys(statusMap).length} recent triages for ${testId} (filtered from ${triageData.length} total)`);
      } else {
        console.log('ℹ️ No existing triage data found');
        setTriageStatus({});
      }
    } catch (error) {
      console.error('Error loading triage status:', error);
      setTriageStatus({});
    }
  };

  const loadComparisonData = async () => {
    try {
      // Load expected output
      const expectedResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/test-cases/${testId}`);
      const expectedJson = await expectedResponse.json();
      setExpectedData(expectedJson);

      // Load test results to get actual decision ID
      const resultsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/test-cases/results/latest`);
      const resultsJson = await resultsResponse.json();
      
      if (resultsJson && resultsJson.test_cases) {
        const testCaseResult = resultsJson.test_cases[testId.toUpperCase()];
        
        if (testCaseResult && testCaseResult.decision_id) {
          // Load actual decision
          const actualResponse = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/decisions/${testCaseResult.decision_id}`
          );
          const actualJson = await actualResponse.json();
          setActualData(actualJson);

          // Load validation results
          const validationResponse = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/test-cases/${testId}/validation`
          );
          if (validationResponse.ok) {
            const validationJson = await validationResponse.json();
            setValidationResults(validationJson);
          }
        }
      }
    } catch (error) {
      console.error('Error loading comparison data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTriageAction = async (error, action, notes) => {
    try {
      const issueData = {
        test_case_id: testId,
        section: error.section,
        error_type: error.type,
        severity: error.severity,
        description: error.description,
        expected_value: error.expected,
        actual_value: error.actual,
        status: action,
        notes: notes || ''
      };

      // Use UPSERT endpoint to prevent duplicates - creates new or updates existing
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/test-issues/upsert`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(issueData)
      });

      if (response.ok) {
        const data = await response.json();
        const issueId = data.id;
        const wasUpdated = data.was_updated;
        const githubIssueNumber = data.github_issue_number;
        
        // Update local state with issue ID for future reference
        setTriageStatus(prev => ({
          ...prev,
          [`${error.section}-${error.type}-${error.description}`]: { 
            action, 
            notes, 
            issueId,
            githubIssueNumber
          }
        }));
        
        // GitHub update is now handled automatically by upsert endpoint
        // It will add a comprehensive comment with full context if GitHub issue exists
        
        setSelectedError(null);
        
        // Optional: Show toast notification
        if (wasUpdated && githubIssueNumber) {
          console.log(`✅ Updated existing issue #${githubIssueNumber} with new triage info`);
        } else if (wasUpdated) {
          console.log('✅ Updated existing triage entry');
        } else {
          console.log('✅ Created new triage entry');
        }
      }
    } catch (error) {
      console.error('Error saving triage decision:', error);
    }
  };

  const handleMouseDown = (e) => {
    // Don't start dragging if clicking the close button
    if (e.target.closest('.modal-close-button')) {
      return;
    }
    
    if (e.target.closest('.modal-drag-handle')) {
      setIsDragging(true);
      const rect = modalRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && modalRef.current) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      setModalPosition({ x: newX, y: newY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  const renderSynthesisWithOptionC = (text) => {
    if (!text) return null;

    // Check for Option C by looking for the emoji marker
    const optionCMarker = '💡 Alternative Path Detected';
    const markerPos = text.indexOf(optionCMarker);
    
    if (markerPos !== -1) {
      // Find the actual box start (look backwards for ┌ or box characters)
      let optionCStart = markerPos;
      const beforeMarker = text.substring(Math.max(0, markerPos - 200), markerPos);
      const boxStartMatch = beforeMarker.lastIndexOf('┌');
      if (boxStartMatch !== -1) {
        // Found the box start, adjust the position
        optionCStart = Math.max(0, markerPos - 200) + boxStartMatch;
      }
      
      // Find the end of Option C box
      const afterMarker = text.substring(optionCStart);
      
      // Look for the end of the box - could be marked by:
      // 1. "This is a" line (older format)
      // 2. Box closing characters like "└" or "│" at the end
      // 3. Next major section or double newline
      let optionCEnd = optionCStart;
      const lines = afterMarker.split('\n');
      let foundEnd = false;
      
      // Look for "This is a" or the last box line
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (line.startsWith('This is a')) {
          optionCEnd = optionCStart + afterMarker.indexOf(lines[i]) + lines[i].length;
          foundEnd = true;
          break;
        }
        // Also check if we hit the end of a box (line with only box chars)
        if (i > 5 && line && /^[│├─┤└┘┌┐]+$/.test(line)) {
          optionCEnd = optionCStart + afterMarker.indexOf(lines[i]) + lines[i].length;
          foundEnd = true;
          break;
        }
      }
      
      // If we didn't find the end, look for next section or take reasonable chunk
      if (!foundEnd) {
        const nextSection = text.indexOf('\n\n\n', optionCStart + 100);
        optionCEnd = nextSection !== -1 ? nextSection : Math.min(optionCStart + 800, text.length);
      }
      
      // Split into before, Option C box, and after
      const beforeOptionC = text.substring(0, optionCStart).trim();
      const optionCBox = text.substring(optionCStart, optionCEnd).trim();
      const afterOptionC = text.substring(optionCEnd).trim();
      
      // Parse Option C content
      const boxLines = optionCBox.split('\n').filter(line => line.trim());
      
      let optionCName = '';
      let whatItIs = '';
      let whyItems = [];
      let solutionType = '';
      
      let currentSection = '';
      
      boxLines.forEach((line, idx) => {
        // First strip ALL box-drawing characters from the line
        const cleaned = line.replace(/[│├─┤└┘┌┐]/g, '').trim();
        
        // Skip emoji line, empty lines
        if (cleaned.includes('Alternative Path Detected') || cleaned.includes('💡') || !cleaned) return;
        
        if (cleaned.startsWith('OPTION C:')) {
          optionCName = cleaned.replace('OPTION C:', '').trim();
          currentSection = 'name';
        } else if (cleaned === 'What it is:') {
          currentSection = 'what';
        } else if (cleaned === 'Why this might work:') {
          currentSection = 'why';
        } else if (cleaned.startsWith('This is a')) {
          solutionType = cleaned;
          currentSection = 'closing';
        } else if (currentSection === 'what' && cleaned) {
          whatItIs += (whatItIs ? ' ' : '') + cleaned;
        } else if (currentSection === 'why' && cleaned.startsWith('•')) {
          whyItems.push(cleaned.substring(1).trim());
        }
      });
      
      return (
        <div className="space-y-4">
          {/* Before Option C */}
          {beforeOptionC.split('\n\n').map((para, idx) => (
            <p key={`before-${idx}`} className="text-gray-800">{para}</p>
          ))}
          
          {/* Option C Box */}
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-300 rounded-xl p-6 my-6">
            <div className="flex items-start gap-3 mb-4">
              <span className="text-3xl flex-shrink-0">💡</span>
              <div>
                <h3 className="text-xl font-bold text-purple-900 mb-1">Alternative Path Detected</h3>
                <p className="text-purple-700 text-sm">A third way forward</p>
              </div>
            </div>
            
            <div className="space-y-4">
              {/* Option C Name */}
              {optionCName && (
                <div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-2">
                    {optionCName}
                  </h4>
                </div>
              )}
              
              {/* What it is */}
              {whatItIs && (
                <div className="bg-white/70 rounded-lg p-4">
                  <h5 className="font-semibold text-gray-900 mb-2">What it is:</h5>
                  <p className="text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{__html: whatItIs.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')}}></p>
                </div>
              )}
              
              {/* Why this might work */}
              {whyItems.length > 0 && (
                <div>
                  <h5 className="font-semibold text-gray-900 mb-3">Why this might work:</h5>
                  <ul className="space-y-2">
                    {whyItems.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-purple-500 mt-1 flex-shrink-0">✓</span>
                        <span className="text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{__html: item.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')}}></span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Solution type */}
              {solutionType && (
                <div className="pt-4 border-t border-purple-200">
                  <p className="text-gray-700 font-semibold">{solutionType}</p>
                </div>
              )}
            </div>
          </div>
          
          {/* After Option C - closing thoughts and alignment question */}
          {afterOptionC && (
            <div className="space-y-3">
              {afterOptionC.split('\n\n').map((para, idx) => (
                <p key={`after-${idx}`} className="text-gray-800 leading-relaxed">{para}</p>
              ))}
            </div>
          )}
        </div>
      );
    }
    
    // No Option C - render normally
    return text.split('\n').map((line, idx) => (
      <p key={idx} className={line.trim() === '' ? 'mb-2' : 'text-gray-800'}>{line}</p>
    ));
  };

  const renderAlignedSection = (sectionName, sectionKey, expectedData, actualData) => {
    if (!expectedData || !actualData) return null;

    // Extract section summaries for both sides
    const expectedSummaries = extractSectionSummaries(expectedData.ai_recommendation || '');
    const actualSummaries = extractSectionSummaries(actualData.ai_recommendation || '');
    
    // Debug logging
    console.log(`[${sectionKey}] Expected vibe summary:`, expectedSummaries.vibe ? expectedSummaries.vibe.substring(0, 50) : 'NULL');
    console.log(`[${sectionKey}] Actual vibe summary:`, actualSummaries.vibe ? actualSummaries.vibe.substring(0, 50) : 'NULL');
    
    // Get the summary for this specific section
    const expectedSummary = expectedSummaries[sectionKey];
    const actualSummary = actualSummaries[sectionKey];
    
    console.log(`[${sectionKey}] Passing to DecisionSection:`, {
      expected: expectedSummary ? expectedSummary.substring(0, 30) : 'NULL',
      actual: actualSummary ? actualSummary.substring(0, 30) : 'NULL'
    });
    
    // Get Logic names for Universe and Vibe consistency
    const expectedLogic = parseLogicData(expectedData.logic_pros || [], expectedData.logic_cons || []);
    const actualLogic = parseLogicData(actualData.logic_pros || [], actualData.logic_cons || []);

    return (
      <div className="grid grid-cols-2 gap-6 mb-8">
        {/* Expected */}
        <DecisionSection
          sectionType={sectionKey}
          decision={expectedData}
          sectionSummary={expectedSummary}
          optionAName={sectionKey !== 'logic' ? expectedLogic.optionA.name : null}
          optionBName={sectionKey !== 'logic' ? expectedLogic.optionB.name : null}
        />
        
        {/* Actual with triage bubbles */}
        <DecisionSection
          sectionType={sectionKey}
          decision={actualData}
          sectionSummary={actualSummary}
          errorIndicators={getErrorIndicators(sectionKey)}
          optionAName={sectionKey !== 'logic' ? actualLogic.optionA.name : null}
          optionBName={sectionKey !== 'logic' ? actualLogic.optionB.name : null}
        />
      </div>
    );
  };

  // Render AI Insights side-by-side
  const renderAlignedAIInsights = (expectedData, actualData) => {
    if (!expectedData || !actualData) return null;

    // Extract summaries
    const expectedSummaries = extractSectionSummaries(expectedData.ai_recommendation || '');
    const actualSummaries = extractSectionSummaries(actualData.ai_recommendation || '');

    return (
      <div className="grid grid-cols-2 gap-6 mb-8">
        {/* Expected AI Insights */}
        <AIInsightsSection
          synthesis={expectedSummaries.synthesis}
        />
        
        {/* Actual AI Insights with triage bubbles */}
        <AIInsightsSection
          synthesis={actualSummaries.synthesis}
          errorIndicators={getErrorIndicators('synthesis')}
        />
      </div>
    );
  };

  const renderAlignedSynthesisSection = (expectedData, actualData) => {
    if (!expectedData || !actualData) return null;

    return (
      <div className="grid grid-cols-2 gap-6">
        {/* Expected */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-3">AI Synthesis</h4>
          <div className="p-4 bg-purple-50 rounded-lg">
            {renderSynthesisWithOptionC(expectedData.ai_recommendation)}
          </div>
        </div>
        
        {/* Actual with error indicators */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
            AI Synthesis
            {getErrorIndicators('synthesis')}
          </h4>
          <div className="p-4 bg-purple-50 rounded-lg">
            {renderSynthesisWithOptionC(actualData.ai_recommendation)}
          </div>
        </div>
      </div>
    );
  };

  const renderDecisionView = (data, title, showErrors = false) => {
    if (!data) return <div className="text-gray-500 text-center py-12">No data available</div>;

    return (
      <div className="bg-white rounded-xl shadow-lg p-6 h-full overflow-y-auto">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">{title}</h2>
        <h3 className="text-xl font-semibold text-gray-800 mb-4">{data.title}</h3>
        
        {/* Use shared DecisionRenderer */}
        <DecisionRenderer
          decision={data}
          mode={showErrors ? "comparison" : "user"}
          validationErrors={showErrors ? buildValidationErrors() : null}
          onErrorClick={showErrors ? openSingleErrorModal : null}
          showErrorBubbles={showErrors}
        />
      </div>
    );
  };

  // Build validation errors in the format DecisionRenderer expects
  const buildValidationErrors = () => {
    if (!validationResults) return null;
    
    // Group errors by section
    const errors = {
      logic: [],
      universe: [],
      vibe: [],
      synthesis: []
    };
    
    // Extract errors from validation results
    // This is a simplified version - will enhance based on testing
    Object.entries(validationResults.sections || {}).forEach(([sectionKey, sectionData]) => {
      if (sectionKey === 'option_c') {
        // Option C errors go in synthesis
        sectionData.issues?.forEach(issue => {
          errors.synthesis.push({
            severity: issue.severity || 'critical',
            description: issue.description,
            type: issue.type
          });
        });
      } else {
        // Regular section errors
        sectionData.missing_themes?.forEach(theme => {
          errors[sectionKey]?.push({
            severity: 'critical',
            description: `Missing theme: ${theme.theme}`,
            type: 'missing'
          });
        });
        
        sectionData.misplaced_items?.forEach(item => {
          errors[sectionKey]?.push({
            severity: 'critical',
            description: `Misplaced: ${item.theme}`,
            type: 'misplaced'
          });
        });
        
        sectionData.incorrect_sentiment?.forEach(item => {
          errors[sectionKey]?.push({
            severity: 'minor',
            description: `Wrong sentiment: ${item.theme}`,
            type: 'sentiment'
          });
        });
      }
    });
    
    return errors;
  };

  const getErrorIndicators = (section) => {
    if (!validationResults || !validationResults.sections) {
      console.log(`[getErrorIndicators] ${section}: No validation results`);
      return null;
    }
    
    console.log(`[getErrorIndicators] ${section}: Processing...`);
    
    // Define bubble style function first (needed by synthesis section)
    const getCircleStyle = (error, idx) => {
      const errorKey = `${error.section}-${error.type}-${error.description}`;
      const triaged = triageStatus[errorKey];
      
      // Severity-based colors for untriaged
      const severityColor = error.severity === 'critical' ? 'red' : 'yellow';
      const severityColorValue = severityColor === 'red' ? '239, 68, 68' : '234, 179, 8'; // red-500 / yellow-500
      
      if (triaged) {
        // Filled circle with status-based color
        const status = triaged.action || triaged;
        let statusColor, hoverColor;
        
        if (status === 'fix_now_major') {
          statusColor = 'bg-red-600';
          hoverColor = 'hover:bg-red-700';
        } else if (status === 'fix_now_minor') {
          statusColor = 'bg-orange-500';
          hoverColor = 'hover:bg-orange-600';
        } else if (status === 'backlog') {
          statusColor = 'bg-blue-500';
          hoverColor = 'hover:bg-blue-600';
        } else if (status === 'acceptable') {
          statusColor = 'bg-green-500';
          hoverColor = 'hover:bg-green-600';
        } else if (status === 'wontfix') {
          statusColor = 'bg-gray-500';
          hoverColor = 'hover:bg-gray-600';
        }
        
        return {
          className: `relative inline-flex items-center justify-center w-8 h-8 rounded-full text-white font-bold shadow-md transition-colors ${statusColor} ${hoverColor}`,
          title: `${error.severity === 'critical' ? 'Critical' : 'Minor'} issue ${idx + 1}: ${error.description}\n✓ Triaged as: ${status.replace('_', ' ').toUpperCase()}${triaged.notes ? `\nNotes: ${triaged.notes}` : ''}`
        };
      } else {
        // Empty circle with severity-based colored border
        return {
          className: `relative inline-flex items-center justify-center w-8 h-8 rounded-full font-bold shadow-md transition-colors bg-white border-2 hover:bg-opacity-10`,
          style: {
            borderColor: `rgb(${severityColorValue})`,
            color: `rgb(${severityColorValue})`
          },
          title: `${error.severity === 'critical' ? 'Critical' : 'Minor'} issue ${idx + 1}: ${error.description}\nClick to triage`
        };
      }
    };
    
    // Collect all errors with their types
    const criticalErrors = [];
    const minorErrors = [];
    
    // Handle Option C / synthesis section specially (it doesn't have its own section data)
    if (section === 'synthesis' && validationResults.sections.option_c) {
      const optionCData = validationResults.sections.option_c;
      
      // Add Option C issues
      optionCData.issues?.forEach((issue, idx) => {
        const errorObj = {
          section: 'synthesis',
          severity: issue.severity || 'critical',
          type: issue.type || 'option_c',
          description: issue.description,
          expected: optionCData.expected === 'present' ? 'Option C should be present' : 'Option C should be absent',
          actual: optionCData.found === 'present' ? 'Option C is present' : 'Option C is missing',
          reason: issue.description,
          index: idx
        };
        
        if (issue.severity === 'critical') {
          criticalErrors.push(errorObj);
        } else {
          minorErrors.push(errorObj);
        }
      });
    }
    
    // Add manual issues for this section BEFORE returning for synthesis
    console.log(`[getErrorIndicators] Section: ${section}, triageStatus keys:`, Object.keys(triageStatus));
    Object.entries(triageStatus).forEach(([key, status]) => {
      if (key.startsWith(`${section}-manual-`)) {
        console.log(`[getErrorIndicators] Found manual issue for ${section}:`, key);
        // Extract description from key
        const description = key.replace(`${section}-manual-`, '');
        
        // Determine severity from status action
        const isCritical = status.action?.includes('fix_now');
        
        const manualError = {
          section,
          severity: isCritical ? 'critical' : 'minor',
          type: 'manual',
          description: description,
          expected: 'Manual issue - see notes',
          actual: 'Manual issue - see notes',
          reason: 'Manually identified false negative',
          index: criticalErrors.length + minorErrors.length,
          // Include existing triage data for editing
          existingNotes: status.notes,
          existingAction: status.action
        };
        
        if (isCritical) {
          criticalErrors.push(manualError);
        } else {
          minorErrors.push(manualError);
        }
      }
    });
    
    // If this is synthesis section, we've already handled Option C above
    // There's no sectionData for 'synthesis', so return early
    if (section === 'synthesis') {
      const allErrors = [...criticalErrors, ...minorErrors];
      if (allErrors.length === 0) return null;
      
      return (
        <div className="flex items-center gap-2 ml-2">
          {criticalErrors.map((error, idx) => {
            const errorKey = `${error.section}-${error.type}-${error.description}`;
            const triaged = triageStatus[errorKey];
            const bubbleStyle = getCircleStyle(error, idx);
            
            return (
              <span 
                key={idx}
                {...bubbleStyle}
                onClick={() => openSingleErrorModal(error)}
              >
                {idx + 1}
              </span>
            );
          })}
          {minorErrors.map((error, idx) => {
            const errorKey = `${error.section}-${error.type}-${error.description}`;
            const triaged = triageStatus[errorKey];
            const bubbleStyle = getCircleStyle(error, idx);
            
            return (
              <span 
                key={idx}
                {...bubbleStyle}
                onClick={() => openSingleErrorModal(error)}
              >
                {idx + 1}
              </span>
            );
          })}
        </div>
      );
    }
    
    // For other sections, get the section data
    const sectionData = validationResults.sections[section];
    if (!sectionData) return null;
    
    // Consolidate errors by theme - multiple issues per theme get combined
    const errorsByTheme = {};
    
    // Add missing themes
    sectionData.missing_themes?.forEach((item, idx) => {
      const themeKey = item.theme;
      if (!errorsByTheme[themeKey]) {
        errorsByTheme[themeKey] = {
          section,
          severity: 'critical',
          theme: item.theme,
          issues: [],
          index: Object.keys(errorsByTheme).length
        };
      }
      errorsByTheme[themeKey].issues.push({
        type: 'missing',
        description: `Missing theme: ${item.theme}`,
        expected: `${item.theme} in ${item.expected_column} (${item.expected_sentiment})`,
        actual: 'Not found',
        reason: 'The AI did not identify this theme from the prompt'
      });
    });

    // Add misplaced items
    sectionData.misplaced_items?.forEach((item, idx) => {
      const themeKey = item.theme;
      if (!errorsByTheme[themeKey]) {
        errorsByTheme[themeKey] = {
          section,
          severity: 'critical',
          theme: item.theme,
          issues: [],
          index: Object.keys(errorsByTheme).length
        };
      }
      errorsByTheme[themeKey].issues.push({
        type: 'misplaced',
        description: `Misplaced: ${item.theme}`,
        expected: `${item.expected_column}`,
        actual: `${item.actual_column}: "${item.text}"`,
        reason: 'The AI placed this item in the wrong column'
      });
    });

    // Add incorrect sentiment
    sectionData.incorrect_sentiment?.forEach((item, idx) => {
      const themeKey = item.theme;
      if (!errorsByTheme[themeKey]) {
        errorsByTheme[themeKey] = {
          section,
          severity: 'minor',
          theme: item.theme,
          issues: [],
          index: Object.keys(errorsByTheme).length
        };
      }
      errorsByTheme[themeKey].issues.push({
        type: 'sentiment',
        description: `Wrong sentiment: ${item.theme}`,
        expected: `${item.expected} sentiment`,
        actual: `${item.actual} sentiment: "${item.text}"`,
        reason: 'The AI identified the correct theme but with the wrong sentiment (pro/con)'
      });
    });
    
    // Convert to array and create consolidated errors
    Object.values(errorsByTheme).forEach(themeError => {
      const consolidated = {
        section,
        severity: themeError.severity,
        type: themeError.issues.map(i => i.type).join('+'),  // e.g., "misplaced+sentiment"
        description: `${themeError.theme}: ${themeError.issues.map(i => i.type).join(' + ')}`,
        expected: themeError.issues.map(i => i.expected).join(' AND '),
        actual: themeError.issues.map(i => i.actual).join(' AND '),
        reason: themeError.issues.map(i => i.reason).join(' ALSO: '),
        index: themeError.index,
        consolidatedIssues: themeError.issues  // Store individual issues for modal
      };
      
      if (themeError.severity === 'critical') {
        criticalErrors.push(consolidated);
      } else {
        minorErrors.push(consolidated);
      }
    });

    // Combine all errors for consecutive numbering
    const allErrors = [...criticalErrors, ...minorErrors];
    
    console.log(`[getErrorIndicators] ${section}: ${allErrors.length} total errors`);
    
    if (allErrors.length === 0) return null;
    
    return (
      <div className="flex flex-wrap gap-2">
        {allErrors.map((error, idx) => {
          const style = getCircleStyle(error, idx);
          return (
            <button
              key={`error-${error.severity}-${idx}`}
              onClick={() => openSingleErrorModal(error)}
              className={style.className}
              style={{ fontSize: '14px', fontFamily: 'system-ui, -apple-system, sans-serif', ...style.style }}
              title={style.title}
            >
              {idx + 1}
            </button>
          );
        })}
      </div>
    );
  };

  const openSingleErrorModal = (error) => {
    setSelectedError({ error });
    setModalPosition({ x: window.innerWidth / 2 - 384, y: 100 }); // Center modal
  };

  const ErrorModal = () => {
    // Always call hooks at the top level
    const [notes, setNotes] = React.useState('');
    const [saveStatus, setSaveStatus] = React.useState(null);
    const [manualSection, setManualSection] = React.useState('logic');
    const [manualSeverity, setManualSeverity] = React.useState('critical');
    const [manualDescription, setManualDescription] = React.useState('');
    const [manualExpected, setManualExpected] = React.useState('');
    const [manualActual, setManualActual] = React.useState('');
    const [manualReason, setManualReason] = React.useState('');

    // Update notes when existingStatus changes (e.g., after loading from DB)
    React.useEffect(() => {
      if (selectedError) {
        const error = selectedError.error;
        const errorKey = `${error.section}-${error.type}-${error.description}`;
        const existingStatus = triageStatus[errorKey];
        
        if (existingStatus?.notes) {
          setNotes(existingStatus.notes);
        } else {
          setNotes('');
        }
      }
    }, [selectedError, triageStatus]);

    // Initialize manual fields when error changes
    React.useEffect(() => {
      if (selectedError) {
        const error = selectedError.error;
        setManualSection(error.section || 'logic');
        setManualSeverity(error.severity || 'critical');
        setManualDescription(error.description || '');
        setManualExpected(error.expected || '');
        setManualActual(error.actual || '');
        setManualReason(error.reason || '');
      }
    }, [selectedError]);

    if (!selectedError) return null;

    const error = selectedError.error;
    const errorKey = `${error.section}-${error.type}-${error.description}`;
    const existingStatus = triageStatus[errorKey];
    
    // For manual issues, allow editing all fields
    const isManualIssue = error.type === 'manual';
    const isNewManualIssue = isManualIssue && !error.description;
    
    // Pre-populate notes for existing manual issues
    React.useEffect(() => {
      if (error.existingNotes) {
        setNotes(error.existingNotes);
      }
    }, [error.existingNotes]);
    
    const modalStyle = modalPosition.x !== 0 || modalPosition.y !== 0
      ? { left: `${modalPosition.x}px`, top: `${modalPosition.y}px`, transform: 'none' }
      : {};

    const handleBackdropClick = () => {
      setSelectedError(null);
    };

    const handleCloseClick = (e) => {
      e.stopPropagation();
      setSelectedError(null);
    };

    const handleAction = (action) => {
      if (isManualIssue) {
        // Validate required fields for manual issues
        if (!manualDescription.trim()) {
          alert('Please enter a description for the manual issue');
          return;
        }
        
        // Create error object with manual fields
        const manualError = {
          section: manualSection,
          severity: manualSeverity,
          type: 'manual',
          description: manualDescription,
          expected: manualExpected,
          actual: manualActual,
          reason: manualReason || 'Manually identified issue'
        };
        
        handleTriageAction(manualError, action, notes);
      } else {
        handleTriageAction(error, action, notes);
      }
    };

    const handleSaveNotesOnly = async () => {
      if (!existingStatus) {
        alert('Please triage this issue first by selecting a status');
        return;
      }
      
      setSaveStatus('saving');
      
      // Update existing triage with new notes
      await handleTriageAction(error, existingStatus.action, notes);
      
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2000);
    };

    const status = existingStatus;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={handleBackdropClick}>
        <div 
          ref={modalRef}
          className="bg-white rounded-xl shadow-2xl w-full mx-4 max-h-[85vh] overflow-y-auto cursor-move"
          style={{ maxWidth: '700px', position: modalPosition.x !== 0 ? 'fixed' : 'relative', ...modalStyle }}
          onClick={e => e.stopPropagation()}
          onMouseDown={handleMouseDown}
        >
          <div className="p-5 border-b border-gray-200 modal-drag-handle sticky top-0 bg-white z-10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <GripVertical className="w-5 h-5 text-gray-400" title="Drag to move" />
                <div className="flex items-center gap-2">
                  {error.severity === 'critical' ? (
                    <AlertCircle className="w-6 h-6 text-red-500" />
                  ) : (
                    <AlertTriangle className="w-6 h-6 text-yellow-500" />
                  )}
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {error.section.toUpperCase()} - {error.severity === 'critical' ? 'Critical' : 'Minor'} Issue
                    </h3>
                    {triageStatus[`${error.section}-${error.type}-${error.description}`]?.issueId && (
                      <p className="text-xs text-gray-500 font-mono">
                        Issue ID: #{triageStatus[`${error.section}-${error.type}-${error.description}`].issueId}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <button onClick={handleCloseClick} className="modal-close-button text-gray-400 hover:text-gray-600 text-2xl leading-none hover:bg-gray-100 w-8 h-8 rounded-full flex items-center justify-center transition-colors">
                ×
              </button>
            </div>
            <p className="text-sm text-gray-500 mt-1 ml-8">💡 Drag this modal to see context behind it</p>
          </div>

          <div className="p-5">
            <div className="border border-gray-200 rounded-lg p-4">
              {isManualIssue ? (
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900 mb-3 text-lg">Manual Issue Entry</h4>
                  
                  {/* Section Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Section</label>
                    <select
                      value={manualSection}
                      onChange={(e) => setManualSection(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="logic">Logic</option>
                      <option value="universe">Universe</option>
                      <option value="vibe">Vibe</option>
                      <option value="synthesis">Synthesis</option>
                    </select>
                  </div>

                  {/* Severity Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Severity</label>
                    <select
                      value={manualSeverity}
                      onChange={(e) => setManualSeverity(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="critical">Critical</option>
                      <option value="minor">Minor</option>
                    </select>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <input
                      type="text"
                      value={manualDescription}
                      onChange={(e) => setManualDescription(e.target.value)}
                      placeholder="Brief description of the issue"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  {/* Expected */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Expected</label>
                    <textarea
                      value={manualExpected}
                      onChange={(e) => setManualExpected(e.target.value)}
                      placeholder="What should have happened"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      rows="2"
                    />
                  </div>

                  {/* Actual */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Actual</label>
                    <textarea
                      value={manualActual}
                      onChange={(e) => setManualActual(e.target.value)}
                      placeholder="What actually happened"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      rows="2"
                    />
                  </div>

                  {/* Reason */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Reason</label>
                    <textarea
                      value={manualReason}
                      onChange={(e) => setManualReason(e.target.value)}
                      placeholder="Why this is an issue"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      rows="2"
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 text-lg">{error.description}</h4>
                  
                  <div className="space-y-2 mb-4 text-sm bg-gray-50 rounded-lg p-4">
                    <div className="flex gap-2">
                      <span className="font-medium text-gray-700 min-w-24">Expected:</span>
                      <span className="text-gray-900">{error.expected}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="font-medium text-gray-700 min-w-24">Actual:</span>
                      <span className="text-gray-900">{error.actual}</span>
                    </div>
                    <div className="flex gap-2">
                      <span className="font-medium text-gray-700 min-w-24">Reason:</span>
                      <span className="text-gray-900">{error.reason}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Notes Field */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes <span className="text-gray-500">(optional)</span>
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any context, reasoning, or action items..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  rows="3"
                />
              </div>

              {status && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-green-800 font-medium">Previously triaged as: {status.action?.replace('_', ' ').toUpperCase() || status.replace('_', ' ').toUpperCase()}</span>
                  </div>
                  {status.notes && (
                    <div className="text-sm text-green-700 mt-1 pl-7">
                      <span className="font-medium">Notes:</span> {status.notes}
                    </div>
                  )}
                  <div className="text-xs text-green-600 mt-2">
                    You can update your triage below
                  </div>
                </div>
              )}

              {/* Show triage buttons always (allow re-triaging) */}
              {(
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => handleAction('fix_now_major')}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center justify-center gap-2"
                  >
                    <AlertCircle className="w-4 h-4" />
                    Fix Now (Major)
                  </button>
                  <button
                    onClick={() => handleAction('fix_now_minor')}
                    className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium flex items-center justify-center gap-2"
                  >
                    <AlertTriangle className="w-4 h-4" />
                    Fix Now (Minor)
                  </button>
                  <button
                    onClick={() => handleAction('backlog')}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
                  >
                    Backlog
                  </button>
                  <button
                    onClick={() => handleAction('acceptable')}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium flex items-center justify-center gap-2"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Acceptable
                  </button>
                  <button
                    onClick={() => handleAction('wontfix')}
                    className="col-span-2 px-4 py-2 bg-gray-400 text-white rounded-lg hover:bg-gray-500 transition-colors font-medium"
                  >
                    Won't Fix
                  </button>
                </div>
              )}

              {/* Save Notes Button for already triaged issues */}
              {status && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <button
                    onClick={handleSaveNotesOnly}
                    disabled={saveStatus === 'saving'}
                    className={`w-full px-4 py-2 rounded-lg font-medium transition-colors ${
                      saveStatus === 'saved'
                        ? 'bg-green-100 text-green-800 border-2 border-green-500'
                        : 'bg-purple-600 text-white hover:bg-purple-700'
                    }`}
                  >
                    {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? '✓ Notes Saved!' : 'Save Notes Only'}
                  </button>
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    Update notes without changing triage status
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex items-center justify-center">
        <Loader className="w-12 h-12 text-purple-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 p-6">
      <div className="max-w-[2000px] mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/admin/test-cases"
            className="flex items-center text-gray-600 hover:text-gray-800 mb-4"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to test cases
          </Link>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Side-by-Side Comparison</h1>
              <p className="text-gray-600">{testId.toUpperCase()} - Click error indicators to triage issues</p>
            </div>
            <button
              onClick={() => {
                // Open modal with blank/default fields for manual entry
                openSingleErrorModal({
                  section: '',
                  severity: 'critical',
                  type: 'manual',
                  description: '',
                  expected: '',
                  actual: '',
                  reason: 'Manually identified false negative',
                  index: 0
                });
              }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              title="Add a manual issue (false negative)"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Manual Issue
            </button>
          </div>
        </div>

        {/* Aligned Section-by-Section Comparison */}
        <div className="space-y-8">
          {/* Headers */}
          <div className="grid grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">📋 Expected Output</h2>
              <h3 className="text-xl font-semibold text-gray-800 mb-4">{expectedData?.title}</h3>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Original Prompt</h4>
                <p className="text-gray-800">{expectedData?.original_prompt}</p>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">🤖 Actual AI Output</h2>
              <h3 className="text-xl font-semibold text-gray-800 mb-4">{actualData?.title}</h3>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Original Prompt</h4>
                <p className="text-gray-800">{actualData?.original_prompt}</p>
              </div>
            </div>
          </div>

          {/* Logic Sections - Side by Side with Triage Bubbles */}
          {renderAlignedSection('Logic', 'logic', expectedData, actualData)}

          {/* Universe Sections - Side by Side with Triage Bubbles */}
          {renderAlignedSection('Universe', 'universe', expectedData, actualData)}

          {/* Vibe Sections - Side by Side with Triage Bubbles */}
          {renderAlignedSection('Vibe', 'vibe', expectedData, actualData)}

          {/* AI Insights - Side by Side with Triage Bubbles */}
          {renderAlignedAIInsights(expectedData, actualData)}
        </div>

        {/* Error Modal */}
        <ErrorModal />
      </div>
    </div>
  );
};

export default TestCaseComparison;
